import React, { useState, useEffect } from "react";
import api from "@/services/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Package,
  Truck,
  Clock,
  CheckCircle,
  TrendingUp,
  Calendar,
  AlertCircle,
  RefreshCw,
  ArrowRight,
} from "lucide-react";

export default function CasaMatrizDashboard() {
  const [stats, setStats] = useState(null);
  const [pedidosHoy, setPedidosHoy] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Get all orders for stats
      const res = await api.get("/pedidos-terceros");
      const pedidos = res.data;
      
      // Calculate stats
      const hoy = new Date().toISOString().split("T")[0];
      const pedidosDeHoy = pedidos.filter(p => p.fecha_entrega === hoy);
      
      const stats = {
        total: pedidos.length,
        porConfirmar: pedidos.filter(p => p.estado === "pendiente" || p.estado === "pagado_pendiente_entrega").length,
        enPreparacion: pedidos.filter(p => p.estado === "confirmado").length,
        enTransito: pedidos.filter(p => p.estado === "en_transito").length,
        entregadosHoy: pedidosDeHoy.filter(p => p.estado === "entregado").length,
        paraHoy: pedidosDeHoy.length,
        valorTotalHoy: pedidosDeHoy.reduce((sum, p) => sum + (p.total || 0), 0),
        unidadesHoy: pedidosDeHoy.reduce((sum, p) => sum + (p.total_unidades || 0), 0),
      };
      
      setStats(stats);
      setPedidosHoy(pedidosDeHoy.filter(p => p.estado !== "entregado" && p.estado !== "cancelado"));
    } catch (error) {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
    }).format(value);
  };

  const getEstadoInfo = (estado) => {
    const estados = {
      pendiente: { label: "Pendiente", color: "amber", icon: Clock },
      pagado_pendiente_entrega: { label: "Pagado - Pend.", color: "blue", icon: CheckCircle },
      confirmado: { label: "En Preparación", color: "indigo", icon: Package },
      en_transito: { label: "En Tránsito", color: "purple", icon: Truck },
    };
    return estados[estado] || { label: estado, color: "gray", icon: Clock };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-purple-600">Cargando dashboard...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="casa-matriz-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl text-torta-dark">Central de Despachos</h1>
          <p className="text-torta-muted">
            {new Date().toLocaleDateString("es-CO", { weekday: "long", day: "numeric", month: "long" })}
          </p>
        </div>
        <Button onClick={fetchData} variant="outline" className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Actualizar
        </Button>
      </div>

      {/* KPIs Principales */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <AlertCircle className="w-8 h-8 opacity-80" />
            <span className="text-3xl font-display">{stats?.porConfirmar || 0}</span>
          </div>
          <p className="font-medium">Por Confirmar</p>
          <p className="text-sm opacity-80">Requieren atención</p>
        </div>

        <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <Package className="w-8 h-8 opacity-80" />
            <span className="text-3xl font-display">{stats?.enPreparacion || 0}</span>
          </div>
          <p className="font-medium">En Preparación</p>
          <p className="text-sm opacity-80">Alistando productos</p>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <Truck className="w-8 h-8 opacity-80" />
            <span className="text-3xl font-display">{stats?.enTransito || 0}</span>
          </div>
          <p className="font-medium">En Tránsito</p>
          <p className="text-sm opacity-80">En camino al cliente</p>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <CheckCircle className="w-8 h-8 opacity-80" />
            <span className="text-3xl font-display">{stats?.entregadosHoy || 0}</span>
          </div>
          <p className="font-medium">Entregados Hoy</p>
          <p className="text-sm opacity-80">Completados</p>
        </div>
      </div>

      {/* Stats del Día */}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
              <Calendar className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-display text-torta-dark">{stats?.paraHoy || 0}</p>
              <p className="text-sm text-torta-muted">Pedidos para Hoy</p>
            </div>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div 
              className="h-full bg-purple-500 rounded-full transition-all"
              style={{ width: `${stats?.paraHoy ? (stats.entregadosHoy / stats.paraHoy * 100) : 0}%` }}
            />
          </div>
          <p className="text-xs text-torta-muted mt-2">
            {stats?.entregadosHoy || 0} de {stats?.paraHoy || 0} completados
          </p>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-display text-torta-dark">{formatCurrency(stats?.valorTotalHoy || 0)}</p>
              <p className="text-sm text-torta-muted">Valor Total Hoy</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <Package className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-display text-torta-dark">{stats?.unidadesHoy || 0}</p>
              <p className="text-sm text-torta-muted">Unidades a Despachar Hoy</p>
            </div>
          </div>
        </div>
      </div>

      {/* Pedidos Pendientes de Hoy */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <div className="p-6 border-b border-gray-100">
          <h2 className="font-display text-xl text-torta-dark">Pedidos Pendientes de Hoy</h2>
          <p className="text-sm text-torta-muted">Pedidos que deben prepararse y despacharse hoy</p>
        </div>

        {pedidosHoy.length === 0 ? (
          <div className="p-8 text-center">
            <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
            <p className="text-torta-muted">¡Todos los pedidos de hoy han sido procesados!</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {pedidosHoy.map((pedido) => {
              const estadoInfo = getEstadoInfo(pedido.estado);
              return (
                <div key={pedido.id} className="p-4 hover:bg-gray-50 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div 
                      className={`w-10 h-10 rounded-lg flex items-center justify-center`}
                      style={{ backgroundColor: estadoInfo.color === "amber" ? "#fef3c7" : estadoInfo.color === "blue" ? "#dbeafe" : estadoInfo.color === "indigo" ? "#e0e7ff" : "#f3e8ff" }}
                    >
                      <estadoInfo.icon 
                        className="w-5 h-5"
                        style={{ color: estadoInfo.color === "amber" ? "#d97706" : estadoInfo.color === "blue" ? "#2563eb" : estadoInfo.color === "indigo" ? "#4f46e5" : "#9333ea" }}
                      />
                    </div>
                    <div>
                      <p className="font-medium text-torta-dark">{pedido.cliente_nombre}</p>
                      <p className="text-sm text-torta-muted">
                        {pedido.total_unidades} unidades • {pedido.hora_entrega}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="font-display text-torta-brown">{formatCurrency(pedido.total)}</p>
                      <p className="text-xs text-torta-muted">{estadoInfo.label}</p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-gray-300" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
